import { ArrowUpTrayIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, useDisclosure } from "@nextui-org/react";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ControlledReactSelect from "../../../../elements/ControlledReactSelect";
import ControlledDropZone from "../../../../elements/ControlledDropZone";
import useListCompany from "../../../../../hooks/useListCompany";
import useSWR from "swr";
import { getFetcher, postFetcher, postMultipartFetcher } from "../../../../../services/api";
import { reportFinalizeValidation } from "../../../../../services/validation/report-preview.validation";
import { isResponseErrorObject } from "../../../../../services/helpers";
import { useEffect, useState } from "react";

export default function ModalFinalizeReport({ endpoint, mutate, selectedCompany, selectedPeriod, setSelectedCompany, setSelectedPeriod }) {
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const {
    control,
    setValue,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ resolver: yupResolver(reportFinalizeValidation) });
  const { dataCompany, isLoadingCompany } = useListCompany();
  const { data: dataPeriod, isLoading: isLoadingPeriod } = useSWR("/api/period/all", getFetcher);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  function handleCloseModal() {
    reset({
      companyId: selectedCompany.value ?? "",
      periodId: selectedPeriod.value ?? "",
      photos: [],
    });
    onClose();
  }

  useEffect(() => {
    if (selectedCompany?.value && selectedPeriod?.value) {
      setValue("companyId", selectedCompany.value);
      setValue("periodId", selectedPeriod.value);
      reset({
        companyId: selectedCompany.value ?? "",
        periodId: selectedPeriod.value ?? "",
        photos: [],
      });
    }
  }, [selectedCompany, selectedPeriod]);

  const handleFormSubmit = (data) => {
    setPendingData(data);
    setIsConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingData) return;
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("companyId", pendingData.companyId);
      formDataToSend.append("periodId", pendingData.periodId);

      pendingData.documents.forEach((file, index) => {
        if (file instanceof File) {
          formDataToSend.append("documents", file);
        }
      });

      await postMultipartFetcher(endpoint, formDataToSend);
      mutate();
      setSelectedCompany({ value: pendingData.companyId, label: dataCompany?.data?.find((item) => item.id === pendingData.companyId).name });
      setSelectedPeriod({ value: pendingData.periodId, label: dataPeriod?.data?.find((item) => item.id === pendingData.periodId).name });
      toast.success("Laporan berhasil difinalisasi.");
      setIsConfirmOpen(false);
      setPendingData(null);
      onClose();
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
      setIsConfirmOpen(false);
      setPendingData(null);
    }
  };

  async function handleOnSubmit(data) {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("companyId", data.companyId);
      formDataToSend.append("periodId", data.periodId);

      data.documents.forEach((file, index) => {
        if (file instanceof File) {
          console.log(`Appending file ${index + 1}:`, file);

          formDataToSend.append("documents", file);
        } else {
          console.error("Invalid file object:", file);
        }
      });

      await postMultipartFetcher(endpoint, formDataToSend);
      mutate();
      setSelectedCompany({ value: data.companyId, label: dataCompany?.data?.find((item) => item.id === data.companyId).name });
      setSelectedPeriod({ value: data.periodId, label: dataPeriod?.data?.find((item) => item.id === data.periodId).name });
      toast.success("Laporan berhasil difinalisasi.");
      onClose();
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
    }
  }

  return (
    <>
      <Button size="sm" className="mt-5" color="warning" onPress={onOpen} startContent={<ArrowUpTrayIcon className="size-4" />}>
        Submit Laporan
      </Button>
      <Modal scrollBehavior="inside" hideCloseButton isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleCloseModal}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader></ModalHeader>
              <ModalBody>
                <form className="flex flex-col gap-3" onSubmit={handleSubmit(handleFormSubmit)}>
                  <div className="flex flex-col items-center mb-6">
                    <QuestionMarkCircleIcon className="size-16 stoke-2 stroke-warning" />
                    <span className="text-2xl font-semibold">Submit Laporan</span>
                    <span className="text-medium text-center">Pastikan laporan telah diisi dengan benar</span>
                  </div>
                  <ControlledReactSelect control={control} defaultValue={selectedCompany} options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))} isLoading={isLoadingCompany} name="companyId" label="Perusahaan" />
                  <ControlledReactSelect defaultValue={selectedPeriod} control={control} options={dataPeriod?.map((item) => ({ label: item.name, value: item.id }))} isLoading={isLoadingPeriod} name="periodId" label="Periode" />
                  <ControlledDropZone control={control} label={"File Surat Kebenaran Laporan"} name="documents" setValue={setValue} placeholder="Tarik atau klik untuk upload file" acceptedFile="pdf" />
                  <div className="flex justify-end gap-2 mt-3">
                    <Button className="bg-line text-ink-500" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                    <Button type="submit" color="warning">
                      Submit
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter className="flex items-center justify-center"></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal isOpen={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <ModalContent>
          <ModalHeader>Konfirmasi Submit Laporan</ModalHeader>
          <ModalBody>
            <p>Apakah Anda yakin ingin submit laporan ini? Data tidak bisa diubah setelah laporan disubmit</p>
          </ModalBody>
          <ModalFooter>
            <Button color="default" variant="faded" onPress={() => setIsConfirmOpen(false)}>
              Batal
            </Button>
            <Button color="warning" onPress={handleConfirmSubmit} isLoading={isSubmitting}>
              Ya, Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
